import { SnackbarProvider } from 'notistack'
import { AuthProvider, CartProvider, FavouritesProvider, ThemeModeProvider, CategoriesProvider, NotificationProvider } from './contexts'
import { SWRConfig } from 'swr'
import { CssBaseline } from '@mui/material'
import {
  normalizeProduct, normalizeProductDetail, normalizeCategory, normalizeBrand,
  normalizeOrder, normalizeAddress, normalizeReview, normalizeOffer, normalizeContentBlock,
  apiClient,
} from 'services/api'

/**
 * Map SWR request paths to their canonical backend client routes.
 * Components use short paths like "/products" or "/categories";
 * this function converts them to the real backend paths.
 */
const toClientRoute = (url) => {
  if (url.startsWith('http')) return url
  const path = url.startsWith('/') ? url.slice(1) : url

  if (path.startsWith('client/')) return `/${path}`
  if (path.startsWith('products')) {
    const qs = path.includes('?') ? path.slice(path.indexOf('?')) : ''
    if (path.startsWith('products/') && !path.startsWith('products?')) {
      return `/client/products/${path.slice('products/'.length)}`
    }
    return `/client/products${qs}`
  }
  if (path.startsWith('categories')) return `/client/${path}`
  if (path.startsWith('brands')) return `/client/${path}`
  if (path.startsWith('orders/my') || path.startsWith('orders?') || path === 'orders') return `/client/orders`
  if (path.startsWith('orders/')) return `/client/orders/${path.slice('orders/'.length)}`
  if (path.startsWith('profile/addresses')) return `/client/profile/addresses`
  if (path.startsWith('profile')) return `/client/profile`
  if (path.startsWith('cart')) return `/client/cart`
  if (path.startsWith('wishlist')) return `/client/wishlist`
  if (path.startsWith('support/tickets/')) return `/client/support/tickets/${path.split('/').pop()}`
  if (path.startsWith('support')) return `/client/support/tickets`
  if (path.startsWith('reviews/product/')) {
    const [base, qs] = path.includes('?') ? [path.slice(0, path.indexOf('?')), path.slice(path.indexOf('?') + 1)] : [path, '']
    const id = base.split('/').pop()
    return qs ? `/client/reviews/product/${id}?${qs}` : `/client/reviews/product/${id}`
  }
  if (path.startsWith('offers/active') || path === 'offers/active') return `/client/offers/active`
  if (path.startsWith('content/page/')) return `/client/content/page/${path.split('/').pop()}`
  if (path.startsWith('content')) {
    const qs = path.includes('?') ? path.slice(path.indexOf('?')) : ''
    return `/client/content${qs}`
  }
  if (path.startsWith('notifications')) return '/client/notifications'
  if (path.startsWith('payment-methods')) return '/client/payment-methods'
  if (path.startsWith('settings')) return `/client/${path}`
  if (path.startsWith('coupons')) return '/client/coupons'
  return `/${path}`
}

/**
 * Normalise the backend response into the shape our components expect.
 */
const normalizeResponse = (url, data) => {
  const path = url.startsWith('/') ? url.slice(1) : url

  // Product filters facets (brands, price range, categories)
  if (/^(client\/)?products\/filters/.test(path)) {
    const facets = data?.data ?? {}
    return { ...data, result: facets, data: facets }
  }

  // Single product detail
  if (/^(client\/)?products\/[^?/]+$/.test(path) && !path.includes('search')) {
    const product = data?.data ?? data
    const normalized = normalizeProductDetail(product)
    return { ...data, result: normalized, data: normalized }
  }

  // Product list (includes search, featured, etc.)
  if (/^(client\/)?products/.test(path)) {
    const items = data?.data ?? data?.result ?? []
    const normalized = Array.isArray(items) ? items.map(normalizeProduct) : []
    return { ...data, result: normalized, data: normalized, pages: data?.pagination?.pages ?? 1, total: data?.pagination?.total ?? 0 }
  }

  // Categories
  if (/^(client\/)?categories/.test(path)) {
    const items = data?.data ?? data?.result ?? []
    const normalized = Array.isArray(items) ? items.map(normalizeCategory) : normalizeCategory(items)
    return { ...data, result: normalized, data: normalized }
  }

  // Brands
  if (/^(client\/)?brands/.test(path)) {
    const items = data?.data ?? data?.result ?? []
    const normalized = Array.isArray(items) ? items.map(normalizeBrand) : normalizeBrand(items)
    return { ...data, result: normalized, data: normalized }
  }

  // Orders list
  if (/^(client\/)?orders$/.test(path) || path === 'orders/my') {
    const orders = (data?.data || data?.orders || data?.result || []).map(normalizeOrder)
    return { ...data, orders, result: orders, data: orders }
  }

  // Single order
  if (/^(client\/)?orders\/\d+/.test(path)) {
    const order = data?.data ?? data
    return { ...data, result: order, data: normalizeOrder(order) }
  }

  // Addresses
  if (/addresses/.test(path)) {
    const items = data?.data ?? data?.result ?? []
    const normalized = Array.isArray(items) ? items.map(normalizeAddress) : normalizeAddress(items)
    return { ...data, result: normalized, data: normalized }
  }

  // Reviews
  if (/reviews/.test(path)) {
    const items = data?.data ?? data?.result ?? []
    const normalized = Array.isArray(items) ? items.map(normalizeReview) : []
    return { ...data, result: normalized, data: normalized, aggregate: data?.aggregate, pagination: data?.pagination }
  }

  // Offers
  if (/offers/.test(path)) {
    const items = data?.data ?? data?.result ?? []
    const normalized = Array.isArray(items) ? items.map(normalizeOffer) : []
    return { ...data, result: normalized, data: normalized }
  }

  // Content blocks
  if (/content/.test(path)) {
    const items = data?.data ?? data?.result ?? []
    const normalized = Array.isArray(items) ? items.map(normalizeContentBlock) : normalizeContentBlock(items)
    return { ...data, result: normalized, data: normalized }
  }

  // Public settings
  if (/settings/.test(path)) {
    return { ...data, result: data?.data, data: data?.data }
  }

  return data
}

const fetcher = async (url) => {
  const clientUrl = toClientRoute(url)
  const { data } = await apiClient.get(clientUrl)
  const normalized = normalizeResponse(url, data)
  
  // Determine what to return based on the response shape
  const path = url.startsWith('/') ? url.slice(1) : url
  
  // Single product detail - return just the product object
  if (/^products\/[^?/]+$/.test(path) && !path.includes('search')) {
    return normalized.data ?? normalized.result ?? normalized
  }
  
  // Lists (has pagination or multiple items) - return full object with .result, .pages, .total
  if (normalized.pages !== undefined || normalized.pagination !== undefined) {
    return normalized
  }
  
  // Offers list — always return wrapped shape for components
  if (/^offers/.test(path)) {
    return normalized
  }

  // Content blocks — always return wrapped shape so components can use data?.data or data?.result
  if (/^content/.test(path)) {
    return normalized
  }

  // For arrays, check if it's wrapped in result
  if (Array.isArray(normalized.result)) {
    return normalized
  }
  
  // Single items (order detail, etc.) - return just the data
  return normalized.data ?? normalized.result ?? normalized
}

const Store = ({ children }) => {
  return (
    <ThemeModeProvider>
      <CssBaseline />
      <SnackbarProvider
        maxSnack={3}
        autoHideDuration={2500}
        preventDuplicate
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <SWRConfig value={{
          fetcher,
          revalidateOnFocus: false,        // don't refetch when tab regains focus
          revalidateOnReconnect: false,    // don't refetch on network reconnect
          shouldRetryOnError: false,       // don't hammer server on errors
        }}>
          <AuthProvider>
            <NotificationProvider>
              <CategoriesProvider>
                <FavouritesProvider>
                  <CartProvider>{children}</CartProvider>
                </FavouritesProvider>
              </CategoriesProvider>
            </NotificationProvider>
          </AuthProvider>
        </SWRConfig>
      </SnackbarProvider>
    </ThemeModeProvider>
  )
}

export default Store
